import { useCallback } from 'react';
import { useFlagsService } from '@atlassian/jira-flags';
import { toFlagId } from '@atlassian/jira-flags/src/services/types';
import { useIntl } from '@atlassian/jira-intl';
import type { Priority } from '../../../common/types';
import { messages } from './messages';

export const useNotifications = () => {
	const DELETE_PRIORITY_ERROR_FLAG_ID = 'deletePriorityError';
	const DELETE_PRIORITY_START_FLAG_ID = 'deletePriorityStart';
	const DELETE_PRIORITY_SUCCESS_FLAG_ID = 'deletePrioritySuccess';
	const REORDER_ERROR_FLAG_ID = 'updateReorderPriorityError';
	const EDIT_ERROR_FLAG_ID = 'updateEditPriorityError';
	const EDIT_SUCCESS_FLAG_ID = 'updateEditPrioritySuccess';
	const CREATE_PRIORITY_SUCCESS_FLAG_ID = 'createPrioritySuccess';
	const CREATE_PRIORITY_ERROR_FLAG_ID = 'createPriorityError';

	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();

	const showFlagForCreatePrioritySuccess = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(CREATE_PRIORITY_SUCCESS_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-create-success.flag',
				type: 'success',
				title: formatMessage(messages.priorityCreateSuccessFlagTitle, {
					priorityName: name,
				}),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForCreatePriorityError = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(CREATE_PRIORITY_ERROR_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-create-error.flag',
				type: 'error',
				title: formatMessage(messages.priorityCreateErrorFlagTitle, {
					priorityName: name,
				}),
				description: formatMessage(messages.priorityCreateErrorFlagBody),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForCreatePriorityErrorUknown = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(CREATE_PRIORITY_ERROR_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-create-error-unknown-reason.flag',
				type: 'error',
				title: formatMessage(messages.priorityCreateErrorFlagTitle, {
					priorityName: name,
				}),
				description: formatMessage(messages.priorityCreateErrorFlagBody),
			});
		},
		[formatMessage, showFlag],
	);

	const showFlagForEditPrioritySuccess = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(EDIT_SUCCESS_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-edit-success.flag',
				type: 'success',
				title: formatMessage(messages.priorityEditSuccessFlagTitle, {
					priorityName: name,
				}),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForEditPriorityError = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(EDIT_ERROR_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-edit-error.flag',
				type: 'error',
				title: formatMessage(messages.priorityEditErrorFlagTitle, {
					priorityName: name,
				}),
				description: formatMessage(messages.priorityEditErrorFlagBody),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForEditPriorityErrorUnknown = useCallback(
		(name: string) => {
			showFlag({
				key: toFlagId(EDIT_ERROR_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-edit-error-default.flag',
				type: 'error',
				title: formatMessage(messages.priorityEditErrorFlagTitle, {
					priorityName: name,
				}),
				description: formatMessage(messages.priorityEditErrorFlagBody),
			});
		},
		[formatMessage, showFlag],
	);

	const showFlagForReorderPriorityError = useCallback(() => {
		showFlag({
			key: toFlagId(REORDER_ERROR_FLAG_ID),
			testId:
				'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-reorder.flag',
			type: 'error',
			title: formatMessage(messages.priorityReorderFailedFlagTitle),
			description: formatMessage(messages.priorityReorderFailedFlagBody),
		});
	}, [formatMessage, showFlag]);

	const showFlagForDeletePriorityStart = useCallback(
		(priority: Priority) => {
			showFlag({
				key: toFlagId(DELETE_PRIORITY_START_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-delete-start.flag',
				title: formatMessage(messages.priorityDeleteStartFlagTitle, {
					priorityName: priority.name,
				}),
				type: 'info',
				description: formatMessage(messages.priorityDeleteStartFlagBody),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForDeletePrioritySuccess = useCallback(
		(priority: Priority) => {
			showFlag({
				key: toFlagId(DELETE_PRIORITY_SUCCESS_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-delete-success.flag',
				title: formatMessage(messages.priorityDeleteSuccessFlagTitle, {
					priorityName: priority.name,
				}),
				type: 'success',
				description: formatMessage(messages.priorityDeleteSuccessFlagBody),
			});
		},
		[formatMessage, showFlag],
	);
	const showFlagForDeletePriorityFailure = useCallback(
		(priority: Priority) => {
			showFlag({
				key: toFlagId(DELETE_PRIORITY_ERROR_FLAG_ID),
				testId:
					'admin-pages-priorities.controllers.priorities-pages.use-priorities-page.on-delete-error.flag',
				title: formatMessage(messages.priorityDeleteErrorFlagTitle, {
					priorityName: priority.name,
				}),
				type: 'error',
				description: formatMessage(messages.priorityDeleteErrorFlagBody),
			});
		},
		[formatMessage, showFlag],
	);

	return {
		showFlagForCreatePrioritySuccess,
		showFlagForCreatePriorityError,
		showFlagForCreatePriorityErrorUknown,
		showFlagForEditPrioritySuccess,
		showFlagForEditPriorityError,
		showFlagForEditPriorityErrorUnknown,
		showFlagForReorderPriorityError,
		showFlagForDeletePrioritySuccess,
		showFlagForDeletePriorityFailure,
		showFlagForDeletePriorityStart,
	};
};
